import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Megaphone,
  Search,
  ArrowUpRight,
  RefreshCcw,
  Settings as SettingsIcon,
  LogOut,
  User,
  FlaskConical,
  Brain,
  FileSpreadsheet,
  Network,
  Sprout,
  ListTree,
  Ban,
  SlidersHorizontal,
  Hash,
  Target,
  BookOpen,
  Inbox,
  Activity,
  Coins,
  Menu,
  X,
} from 'lucide-react'
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import { clearToken } from '@/api/client'
import { BUILD_TIME, footerBuildLabel } from '@/lib/buildInfo'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import ErrorBoundary from './ErrorBoundary'
import { Toaster } from './Toaster'

type NavItem = {
  to: string
  icon: typeof LayoutDashboard
  label: string
  enabled: boolean
}

type NavSection = {
  header: string
  items: NavItem[]
}

const sections: NavSection[] = [
  {
    header: 'Steuern',
    items: [
      { to: '/', icon: Inbox, label: 'Freigaben', enabled: true },
      { to: '/performance', icon: LayoutDashboard, label: 'Performance-Übersicht', enabled: true },
      { to: '/campaigns', icon: Megaphone, label: 'Kampagnen', enabled: true },
      { to: '/portfolios', icon: Network, label: 'Portfolios', enabled: true },
      { to: '/keywords', icon: Search, label: 'Keywords', enabled: true },
      { to: '/asin-targeting', icon: Target, label: 'ASIN-Targeting', enabled: true },
    ],
  },
  {
    header: 'Optimieren',
    items: [
      { to: '/harvest', icon: Sprout, label: 'Auto-Harvest', enabled: true },
      { to: '/harvest?tab=pipeline', icon: ListTree, label: 'Pipeline', enabled: true },
      { to: '/harvest?tab=negatives', icon: Ban, label: 'Negatives', enabled: true },
      { to: '/rules', icon: SlidersHorizontal, label: 'Regelwerk', enabled: true },
      { to: '/experiments', icon: FlaskConical, label: 'Experimente', enabled: true },
      { to: '/learning', icon: Brain, label: 'Learning', enabled: true },
      { to: '/promotions', icon: ArrowUpRight, label: 'Promotions', enabled: true },
    ],
  },
  {
    header: 'Umsetzen',
    items: [
      { to: '/naming', icon: Hash, label: 'Naming-Generator', enabled: true },
      { to: '/launch-review', icon: Megaphone, label: 'Kampagnenstart', enabled: true },
      { to: '/wave-economics', icon: Coins, label: 'Wellen-Economics', enabled: true },
      { to: '/family-economics', icon: Coins, label: 'Familien-Economics', enabled: true },
      { to: '/bulk-import', icon: FileSpreadsheet, label: 'Bulk-Import', enabled: true },
      { to: '/reconcile', icon: Activity, label: 'Hängende Leases', enabled: true },
      { to: '/sync', icon: RefreshCcw, label: 'Sync-Center', enabled: true },
      { to: '/settings', icon: SettingsIcon, label: 'Einstellungen', enabled: true },
    ],
  },
  {
    header: 'Hilfe',
    items: [
      { to: '/wiki', icon: BookOpen, label: 'Handbuch', enabled: true },
    ],
  },
]

// Scoped Dark-Theme nur fuer Sidebar — bricht :root nicht.
const sidebarStyle: CSSProperties = {
  ['--sidebar-background' as string]: 'oklch(0.18 0.03 255)',
  ['--sidebar-foreground' as string]: 'oklch(0.96 0 0)',
  ['--sidebar-accent' as string]: 'oklch(0.26 0.04 255)',
  ['--sidebar-accent-foreground' as string]: 'oklch(1 0 0)',
  ['--sidebar-border' as string]: 'oklch(1 0 0 / 0.08)',
  ['--muted-foreground' as string]: 'oklch(0.72 0 0)',
} as CSSProperties

function getUserName(): string {
  try {
    const raw = localStorage.getItem('amz_ads_user')
    if (!raw) return 'Benutzer'
    const user = JSON.parse(raw) as { name?: string }
    return user.name ?? 'Benutzer'
  } catch {
    return 'Benutzer'
  }
}

function isQueryItemActive(itemTo: string, currentUrl: string): boolean {
  return currentUrl === itemTo
}

function NavItemRow({ item }: { item: NavItem }) {
  const location = useLocation()
  const Icon = item.icon

  const baseClasses =
    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--ring)]'
  const activeClasses =
    'bg-[var(--sidebar-accent)] text-[var(--sidebar-accent-foreground)]'
  const idleClasses =
    'text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)]'

  if (!item.enabled) {
    return (
      <span
        className={cn(
          baseClasses,
          'cursor-not-allowed text-[var(--sidebar-foreground)] opacity-40',
        )}
        aria-disabled="true"
      >
        <Icon className="h-4 w-4" />
        {item.label}
        <span className="ml-auto text-[10px] uppercase tracking-wider text-[var(--muted-foreground)]">
          Soon
        </span>
      </span>
    )
  }

  if (item.to.includes('?')) {
    const currentUrl = `${location.pathname}${location.search}`
    const active = isQueryItemActive(item.to, currentUrl)
    return (
      <Link
        to={item.to}
        className={cn(baseClasses, active ? activeClasses : idleClasses)}
      >
        <Icon className="h-4 w-4" />
        {item.label}
      </Link>
    )
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(baseClasses, isActive ? activeClasses : idleClasses)
      }
    >
      <Icon className="h-4 w-4" />
      {item.label}
    </NavLink>
  )
}

export default function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const sidebarId = useId()
  const menuRef = useRef<HTMLButtonElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => { setMobileOpen(false) }, [location])

  useEffect(() => {
    if (!mobileOpen) return
    const sidebar = sidebarRef.current!
    const desktop = window.matchMedia?.('(min-width: 768px)')
    closeRef.current?.focus()
    function keydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        setMobileOpen(false)
      } else if (event.key === 'Tab') {
        const items = sidebar.querySelectorAll<HTMLElement>(':is(a[href], button:not(:disabled))')
        const first = items[0], last = items[items.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
      }
    }
    function keepFocus(event: FocusEvent) {
      if (!sidebar.contains(event.target as Node)) closeRef.current?.focus()
    }
    function resize() { if (desktop?.matches) setMobileOpen(false) }
    document.addEventListener('keydown', keydown, true)
    document.addEventListener('focusin', keepFocus)
    desktop?.addEventListener('change', resize)
    return () => {
      document.removeEventListener('keydown', keydown, true)
      document.removeEventListener('focusin', keepFocus)
      desktop?.removeEventListener('change', resize)
      if (!desktop?.matches) menuRef.current?.focus()
      else if (document.activeElement === closeRef.current) {
        sidebar.querySelector<HTMLAnchorElement>('nav a[href]')?.focus()
      }
    }
  }, [mobileOpen])

  function handleLogout() {
    clearToken()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex h-dvh min-w-0 flex-col overflow-x-hidden md:flex-row">
      <header inert={mobileOpen} style={sidebarStyle}
        className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--sidebar-border)] bg-[var(--sidebar-background)] px-4 text-[var(--sidebar-foreground)] md:hidden">
        <button ref={menuRef} type="button" aria-label="Navigation öffnen" aria-expanded={mobileOpen} aria-controls={sidebarId}
          onClick={() => setMobileOpen(true)}
          className="rounded-md p-2 hover:bg-[var(--sidebar-accent)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--ring)]">
          <Menu className="size-4" aria-hidden="true" />
        </button>
        <span className="text-sm font-semibold">Stahlia Ads</span>
      </header>
      {mobileOpen && <div aria-hidden="true" data-testid="navigation-backdrop"
        onClick={() => setMobileOpen(false)} style={sidebarStyle}
        className="fixed inset-0 z-40 bg-[var(--sidebar-background)] opacity-60 md:hidden" />}
      <div
        id={sidebarId} ref={sidebarRef} role={mobileOpen ? 'dialog' : undefined}
        aria-modal={mobileOpen || undefined} aria-label={mobileOpen ? 'Navigation' : undefined}
        className={cn('w-60 shrink-0 flex-col border-r border-[var(--sidebar-border)] bg-[var(--sidebar-background)] text-[var(--sidebar-foreground)] md:static md:flex',
          mobileOpen ? 'fixed inset-y-0 left-0 z-50 flex' : 'hidden')}
        style={sidebarStyle}
      >
        <aside className="flex min-h-0 flex-1 flex-col">
          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--sidebar-border)] px-4">
            <button ref={closeRef} type="button" aria-label="Navigation schließen" onClick={() => setMobileOpen(false)}
              className="rounded-md p-2 hover:bg-[var(--sidebar-accent)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--ring)] md:hidden">
              <X className="size-4" aria-hidden="true" />
            </button>
            <Megaphone className="h-5 w-5 text-orange-500" />
            <span className="text-sm font-semibold text-[var(--sidebar-foreground)]">
              Stahlia Ads
            </span>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {sections.map((section) => (
              <div key={section.header} className="mt-4 first:mt-0">
                <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
                  {section.header}
                </div>
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <NavItemRow key={item.to} item={item} />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="border-t border-[var(--sidebar-border)] p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
                <User className="h-3.5 w-3.5" />
                <span className="max-w-[120px] truncate">{getUserName()}</span>
              </div>
              <button
                onClick={handleLogout}
                className="rounded-md p-1 text-[var(--muted-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--sidebar-foreground)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                title="Abmelden"
                aria-label="Abmelden"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              {footerBuildLabel(BUILD_TIME, IS_DEMO_MODE)}
            </p>
          </div>
        </aside>
      </div>

      <main inert={mobileOpen} className="min-h-0 min-w-0 flex-1 overflow-auto bg-[var(--background)] p-4 md:p-6">
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Globale Toast-Ausgabe (Mutations-Fehler app-weit sichtbar) */}
      <Toaster />
    </div>
  )
}
